import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Text, cn } from "@fmc-digital/ui";
import { SuperscriptText } from "../../atoms/SuperscriptText";
import {
  registrationValuePropBodySlotStyles,
  registrationValuePropCardInnerStyles,
  registrationValuePropCardStyles,
  registrationValuePropCopyBlockStyles,
  registrationValuePropHeadlineSlotStyles,
  registrationValuePropHeadlineStyles,
  registrationValuePropPhoneStageStyles,
  registrationValuePropSlideImgStyles,
} from "../../organisms/RegistrationValueProposition/registration-value-proposition.styles";

export type ValuePropositionSlideCardProps = {
  /** Per-slide image under `public/images/registration/` (see value-proposition-slides constants). */
  readonly slideImageSrc: string;
  readonly slideImageAlt: string;
  readonly slideIndex: number;
  readonly headline: string;
  readonly description: string;
  /** Pause auto-advance while the pointer is over the image stage (not the headline/body). */
  readonly onPhonePointerEnter: () => void;
  readonly onPhonePointerLeave: () => void;
};

/**
 * Value proposition card: visual stage + current slide copy (Figma inner slide 566×665 in card).
 */
export function ValuePropositionSlideCard({
  slideImageSrc,
  slideImageAlt,
  slideIndex,
  headline,
  description,
  onPhonePointerEnter,
  onPhonePointerLeave,
}: ValuePropositionSlideCardProps) {
  const skipFadeOnMount = useRef(true);
  /** Bumped on fade cleanup so any in-flight rAF from a prior slide/session cannot schedule work or call setState. */
  const fadeGenerationRef = useRef(0);
  const [slideOpaque, setSlideOpaque] = useState(true);
  /** Image stage only — listeners via effect keep JSX free of handler props (a11y / static analysis). */
  const imageStageHoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = imageStageHoverRef.current;
    if (!el) return;
    el.addEventListener("pointerenter", onPhonePointerEnter);
    el.addEventListener("pointerleave", onPhonePointerLeave);
    return () => {
      el.removeEventListener("pointerenter", onPhonePointerEnter);
      el.removeEventListener("pointerleave", onPhonePointerLeave);
    };
  }, [onPhonePointerEnter, onPhonePointerLeave]);

  useLayoutEffect(() => {
    if (skipFadeOnMount.current) {
      skipFadeOnMount.current = false;
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      // No fade: keep (or restore) full opacity — e.g. after a torn-down fade left slideOpaque false.
      setSlideOpaque(true);
      return;
    }
    const fadeGeneration = ++fadeGenerationRef.current;
    setSlideOpaque(false);

    const pending = { rafOut: null as number | null, rafIn: null as number | null };
    const cancelPendingFrames = () => {
      if (pending.rafOut != null) {
        window.cancelAnimationFrame(pending.rafOut);
        pending.rafOut = null;
      }
      if (pending.rafIn != null) {
        window.cancelAnimationFrame(pending.rafIn);
        pending.rafIn = null;
      }
    };

    pending.rafOut = window.requestAnimationFrame(() => {
      pending.rafOut = null;
      if (fadeGenerationRef.current !== fadeGeneration) return;
      pending.rafIn = window.requestAnimationFrame(() => {
        pending.rafIn = null;
        if (fadeGenerationRef.current !== fadeGeneration) return;
        setSlideOpaque(true);
      });
    });

    return () => {
      fadeGenerationRef.current += 1;
      cancelPendingFrames();
    };
  }, [slideIndex]);

  return (
    <div className={cn(registrationValuePropCardStyles())}>
      <div
        className={cn(
          registrationValuePropCardInnerStyles(),
          "transition-opacity duration-[400ms] ease-out motion-reduce:transition-none",
          slideOpaque ? "opacity-100" : "opacity-0",
        )}
      >
        <div
          ref={imageStageHoverRef}
          className={registrationValuePropPhoneStageStyles()}
          data-testid="value-prop-carousel-hover-region"
        >
          <img
            src={slideImageSrc}
            alt={slideImageAlt}
            className={registrationValuePropSlideImgStyles()}
            width={313}
            height={300}
            loading="eager"
            decoding="async"
          />
        </div>

        <div
          key={slideIndex}
          className={registrationValuePropCopyBlockStyles()}
          data-slide-index={slideIndex}
          aria-live="polite"
          aria-atomic="true"
        >
          <div className={registrationValuePropHeadlineSlotStyles()}>
            <h1
              key={`value-prop-h1-${slideIndex}`}
              className={registrationValuePropHeadlineStyles()}
              data-testid="value-prop-active-headline"
            >
              {headline}
            </h1>
          </div>
          <div className={registrationValuePropBodySlotStyles()}>
            <Text
              key={`value-prop-body-${slideIndex}`}
              variant="bodyResponsive"
            >
              <SuperscriptText text={description} />
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
